<template>
  <div>
    <h1 class="text-[26px] font-bold tracking-tight text-ink m-0 mb-6 max-md:text-[22px]">Team</h1>

    <skeleton-team v-if="isLoading"></skeleton-team>

    <div v-else class="grid grid-cols-2 gap-4 max-md:grid-cols-1">
      <div
        v-for="manager in managerCards"
        :key="manager.value"
        class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <!-- Manager header -->
        <div class="flex items-center gap-3 mb-5">
          <img v-if="manager.photo" :src="manager.photo" :alt="manager.text" class="w-10 h-10 rounded-full object-cover shrink-0">
          <div v-else class="w-10 h-10 rounded-full bg-[#f0eef5] text-primary flex items-center justify-center font-bold text-sm shrink-0">{{ manager.text.charAt(0) }}</div>
          <div>
            <div class="text-[15px] font-semibold text-ink leading-tight">{{ manager.text }}</div>
            <div class="flex items-center gap-3 mt-0.5">
              <span class="text-xs text-muted">{{ manager.activeCases }} {{ manager.activeCases === 1 ? 'case' : 'cases' }}</span>
              <span class="text-placeholder">&middot;</span>
              <span class="text-xs text-muted">{{ manager.openRequirements }} open req.</span>
            </div>
          </div>
        </div>

        <!-- Assigned applicants -->
        <div v-if="manager.applicants.length === 0" class="text-[13px] text-placeholder py-3">No assigned cases</div>
        <div v-else class="flex flex-col gap-2">
          <router-link
            v-for="a in manager.applicants"
            :key="a.id"
            :to="{ name: 'admin-applicant', params: { id: a.id } }"
            class="flex items-center gap-2.5 py-2 px-2.5 rounded-lg no-underline transition-colors duration-150 hover:bg-canvas hover:no-underline">
            <img v-if="a.picture" :src="'/static/images/clients/' + a.picture" :alt="a.name" class="w-7 h-7 rounded-full object-cover shrink-0">
            <div v-else class="w-7 h-7 rounded-full bg-[#f0eef5] text-primary flex items-center justify-center font-bold text-[11px] shrink-0">{{ a.name.charAt(0) }}</div>
            <div class="flex-1 min-w-0">
              <span class="text-[13px] font-medium text-ink truncate block leading-tight">{{ a.name }}</span>
              <div class="flex items-center gap-2 mt-1">
                <div class="flex-1 h-1 bg-[#f0eef5] rounded-sm overflow-hidden">
                  <div class="h-full rounded-sm transition-[width] duration-300" :style="{ width: a.progress + '%' }" :class="a.progress === 100 ? 'bg-[#2d7a0e]' : 'bg-primary'"></div>
                </div>
                <span class="text-[10px] font-semibold text-muted min-w-[28px] text-right">{{ a.progress }}%</span>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getCompletedPercentage } from '@/services/applicant-helpers'
import { PHOTOS } from '@/services/photos'
import SkeletonTeam from '@/components/UIComponents/SkeletonTeam'

export default {
  name: 'admin-team',
  components: { SkeletonTeam },
  data () {
    return {
      isLoading: false
    }
  },
  computed: {
    ...mapState({
      applicants: state => state.applicants.applicants,
      managers: state => state.managers.managers.filter(m => !m.hidden)
    }),
    activeApplicants () {
      if (!this.applicants) return []
      return this.applicants.filter(a => a.isActive)
    },
    managerCards () {
      if (!this.managers) return []
      return this.managers.map(m => {
        const assigned = this.activeApplicants.filter(a =>
          a.availableTo && a.availableTo.includes(m.value)
        )
        let openReqs = 0
        assigned.forEach(a => {
          if (a.requirements) {
            a.requirements.forEach(r => {
              if (r.assignedTo && r.assignedTo.includes(m.value) && r.status !== 'complete' && r.status !== 'submitted' && r.status !== 'sentToApplicant') {
                openReqs++
              }
            })
          }
        })
        return {
          text: m.text,
          value: m.value,
          photo: PHOTOS[m.value] || null,
          activeCases: assigned.length,
          openRequirements: openReqs,
          applicants: assigned.map(a => ({
            id: a.id,
            name: ((a.firstName || '') + ' ' + (a.lastName || '')).trim(),
            picture: a.picture || null,
            progress: getCompletedPercentage(a)
          }))
        }
      })
    }
  },
  created () {
    if (!this.applicants || this.applicants.length === 0) {
      this.isLoading = true
      this.$store.dispatch('GET_APPLICANTS').then(() => {
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
      })
    }
  }
}
</script>
